/**
 * SignUpOrLogIn: combined phone-based sign up / log in flow, rendered at
 * /signUpLogin (ProtectedRoute, requiresLogin=false) and also embedded by
 * pages/logged_out/LoggedOutHome at "/". Three staged inputs: phone (SMS code
 * request), code verification, and username creation for new accounts, calling
 * api/auth (AuthAPI.submitPhone, submitCode, submitUsername). On success it
 * stores the user in contexts/UserContext and navigates to /account; errors
 * surface via contexts/ErrorContext. Uses utils/auth phone/username formatters
 * and links to the privacy policy and terms of service from constants/links.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthAPI } from "@/api/auth";
import { formatPhoneNumberForInput, sanitizeUsername, stripToPlusAndDigits } from "@/utils/auth";
import { setUserContext, useUserDispatch } from "@/contexts/UserContext";
import { privacyPolicyLink, termsOfServiceLink } from "@/constants/links";
import { useError } from "@/contexts/ErrorContext";

type Stage = "phone" | "code" | "username";

interface SignUpOrLogInProps {}

const SignUpOrLogIn: React.FC<SignUpOrLogInProps> = () => {
    const [stage, setStage] = useState<Stage>("phone");
    const [countryCode, setCountryCode] = useState<string>("+1");
    const [phone, setPhone] = useState<string>("+1 ");
    const [code, setCode] = useState<string>("");
    const [username, setUsername] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const { showError } = useError();
    const userDispatch = useUserDispatch();
    const navigate = useNavigate();

    const handlePhoneSubmit = async () => {
        setLoading(true);
        try {
            await AuthAPI.submitPhone(stripToPlusAndDigits(phone));
            setStage("code");
            setCode("");
        } catch (errMessage) {
            showError(errMessage as string);
        } finally {
            setLoading(false);
        }
    };

    const handleCodeSubmit = async () => {
        setLoading(true);
        try {
            const { user } = await AuthAPI.submitCode(stripToPlusAndDigits(phone), code);
            if (user) {
                setUserContext(userDispatch, user);
                navigate("/account");
            } else {
                setStage("username");
            }
        } catch (errMessage) {
            showError(errMessage as string);
        } finally {
            setLoading(false);
        }
    };

    const handleUsernameSubmit = async () => {
        setLoading(true);
        try {
            const { user } = await AuthAPI.submitUsername(stripToPlusAndDigits(phone), username);
            if (user) {
                setUserContext(userDispatch, user);
                navigate("/account");
            } else {
                console.error("User should not be null");
                showError("An unexpected error occurred. Please try again.");
            }
        } catch (errMessage) {
            showError(errMessage as string);
        } finally {
            setLoading(false);
        }
    };

    const inputClasses =
        "w-48 px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:border-black focus:outline-none font-porsui tracking-widest ";
    const buttonClasses =
        "w-48 flex justify-center py-1.5 px-3 text-sm font-medium text-black bg-white border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 disabled:opacity-50 disabled:cursor-not-allowed font-porsui ";

    return (
        <div className="bg-slate-50 flex flex-col items-center justify-center w-full h-full relative">
            <div className="max-w-md w-full flex flex-col items-center space-y-8 p-8">
                <img src="/p-logo.png" className="mt-[-4px] w-16 object-contain" />
                <div className="text-center">
                    <h2 className="text-2xl text-black font-porsui font-bold">sign up/log in</h2>
                </div>

                <div className="w-48 mt-8 space-y-6 relative flex flex-col items-center">
                    {/* Phone number input */}
                    <div
                        className={`transition-opacity duration-500 ${
                            stage !== "phone"
                                ? "opacity-0 invisible absolute"
                                : "opacity-100 visible"
                        }`}
                    >
                        <div className="flex items-center space-x-2 w-48">
                            <select
                                className="w-10 h-8 flex-shrink-0 text-sm border border-gray-200 rounded-md focus:border-black focus:outline-none font-porsui tracking-widest bg-white appearance-none cursor-pointer text-center"
                                style={{ lineHeight: "1.5rem", padding: "0 0.5rem" }}
                                value={countryCode}
                                onChange={(e) => {
                                    setCountryCode(e.target.value);
                                    setPhone(e.target.value + " ");
                                }}
                                disabled={loading || stage !== "phone"}
                            >
                                <option value="+1">+1</option>
                            </select>
                            <div className="flex-1 min-w-0">
                                <input
                                    type="tel"
                                    id="phone"
                                    className="w-full px-1.5 py-1 text-sm border border-gray-200 rounded-md focus:border-black focus:outline-none font-porsui tracking-widest text-center"
                                    value={phone.replace(countryCode + " ", "")}
                                    onChange={(e) => {
                                        const formattedWithPrefix = formatPhoneNumberForInput(
                                            countryCode + " " + e.target.value
                                        );
                                        setPhone(formattedWithPrefix);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !loading) {
                                            handlePhoneSubmit();
                                        }
                                    }}
                                    disabled={loading || stage !== "phone"}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Code input */}
                    <div
                        className={`w-48 transition-opacity duration-500 ${
                            stage !== "code"
                                ? "opacity-0 invisible absolute"
                                : "opacity-100 visible"
                        }`}
                    >
                        <input
                            type="text"
                            id="code"
                            className={inputClasses + "text-center"}
                            maxLength={6}
                            placeholder="enter code"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !loading) {
                                    handleCodeSubmit();
                                }
                            }}
                            disabled={loading || stage !== "code"}
                        />
                    </div>

                    {/* Username input */}
                    <div
                        className={`w-48 transition-opacity duration-500 ${
                            stage !== "username"
                                ? "opacity-0 invisible absolute"
                                : "opacity-100 visible"
                        }`}
                    >
                        <input
                            type="text"
                            id="username"
                            className={inputClasses + "text-center"}
                            placeholder="choose username"
                            value={username}
                            onChange={(e) => setUsername(sanitizeUsername(e.target.value))}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !loading) {
                                    handleUsernameSubmit();
                                }
                            }}
                            disabled={loading || stage !== "username"}
                        />
                        <div className="w-48 mt-2 text-xs text-zinc-500 font-porsui flex flex-col text-center items-center space-y-0.5">
                            <span>can only contain letters, numbers, periods, and underscores</span>
                            <span>username can't be changed</span>
                        </div>
                    </div>

                    {/* Submit button */}
                    <button
                        onClick={() => {
                            if (stage === "phone") handlePhoneSubmit();
                            else if (stage === "code") handleCodeSubmit();
                            else if (stage === "username") handleUsernameSubmit();
                        }}
                        disabled={loading}
                        className={buttonClasses}
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4" />
                        ) : stage === "phone" ? (
                            "send code"
                        ) : stage === "code" ? (
                            "submit code"
                        ) : (
                            "create account"
                        )}
                    </button>

                    {/* Resend code button for code stage */}
                    {stage === "code" && (
                        <button
                            onClick={handlePhoneSubmit}
                            disabled={loading}
                            className="mt-2 text-zinc-500 hover:text-zinc-600 focus:outline-none text-sm font-porsui"
                        >
                            resend code
                        </button>
                    )}

                    {stage === "phone" && (
                        <div className="mt-2 text-xs text-zinc-500 font-porsui flex flex-col w-full items-center text-center space-y-1">
                            <span>only u.s. #'s allowed for now</span>
                            <span>by continuing you consent to receiving SMS from us</span>
                            <span>will never be shared or public</span>
                            <span>reply STOP anytime to opt out</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Privacy Policy and Terms of Service Links */}
            <div className="absolute bottom-4 left-0 right-0 px-4 flex flex-wrap justify-center items-center space-x-1 text-xs text-black font-porsui">
                <a
                    href={privacyPolicyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                >
                    privacy policy
                </a>
                <span>&</span>
                <a
                    href={termsOfServiceLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                >
                    terms of service
                </a>
            </div>
        </div>
    );
};

export default SignUpOrLogIn;
